import { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useResource, formatDateTime } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { EntityForm } from "../../components/EntityForm";
import { type QuarantineRow, streamOf, isFeedbackForm, feedbackTypeOf } from "../../lib/intakeStreams";
import { senderOf, detailsOf, feedbackPrefillFromPayload, containsLink } from "../../lib/intakeDisplay";
import { feedbackRegisterFields } from "../../lib/feedbackIntake";

/**
 * Website forms with no queue of their own land here — newsletter signups,
 * feedback, complaints. Referrals go to the Referrals page, enquiries (contact
 * / callback) to the Enquiry queue and careers forms to Applicants; whatever
 * is left groups by form here, each with an audited dismiss. Rows share the
 * referral quarantine table; only the presentation and routing differ.
 *
 * Feedback, complaint and compliment submissions get one extra action: promote
 * into the feedback register, which is where they earn a TC-FBK / TC-CML /
 * TC-CMP reference. They stay quarantined until someone does that on purpose —
 * a complaints register is an audited record, so spam must not be able to file
 * itself into one.
 */

export const WebsiteInbox = () => {
    const { user } = useAuth();
    const canDecide = user?.role === "admin" || user?.role === "manager";

    const quarantine = useResource<QuarantineRow[]>(canDecide ? "/intake/quarantine" : null);
    const participants = useResource<Array<{ id: string; full_name: string }>>(canDecide ? "/participants" : null);
    const [dismissing, setDismissing] = useState<QuarantineRow | null>(null);
    const [recording, setRecording] = useState<QuarantineRow | null>(null);
    const [open, setOpen] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    /**
     * Promote = create the feedback record through the ordinary validated
     * route, which mints the reference, then stamp the quarantine row with
     * what it became so the inbox empties honestly.
     */
    const recordInRegister = async (item: QuarantineRow, saved?: unknown) => {
        const feedbackId = (saved as { id?: string } | undefined)?.id;
        setRecording(null);
        if (!feedbackId) { quarantine.reload(); return; }
        try {
            await api.post(`/intake/quarantine/${item.id}/promote-feedback`, { feedbackId });
        } catch (err) {
            setError(err instanceof Error ? err.message
                : "Recorded it in the register but could not clear the inbox item");
        }
        quarantine.reload();
    };

    // Referrals and enquiries have their own pages; the inbox holds only what's
    // left. The classifier is shared with the intake trigger, so nothing shows
    // in two places.
    const nonReferral = useMemo(
        () => (quarantine.data ?? []).filter((q) => streamOf(q.form_name) === "other"),
        [quarantine.data]
    );

    const groups = useMemo(() => {
        const byForm = new Map<string, QuarantineRow[]>();
        for (const row of nonReferral) {
            const key = (row.form_name ?? "unknown").toLowerCase();
            byForm.set(key, [...(byForm.get(key) ?? []), row]);
        }
        return [...byForm.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    }, [nonReferral]);

    if (!canDecide) return <div className="error-banner">This page is for admins and managers.</div>;
    if (quarantine.error) return <div className="error-banner">{quarantine.error}</div>;

    return (
        <div style={{ display: "grid", gap: "1rem" }}>
            {dismissing && (
                <ConfirmDialog
                    title={`Dismiss this ${dismissing.form_name ?? "website"} submission?`}
                    message="It stays in the quarantine record with your reason — nothing is deleted."
                    confirmLabel="Dismiss" requireReason reasonLabel="Reason"
                    request={{ method: "post", path: `/intake/quarantine/${dismissing.id}/reject` }}
                    onClose={() => setDismissing(null)}
                    onDone={() => { setDismissing(null); quarantine.reload(); }} />
            )}

            {recording && (
                <EntityForm
                    title={`Record this ${feedbackTypeOf(recording.form_name)} in the register`}
                    path="/feedback-submissions"
                    fields={feedbackRegisterFields({ participants: participants.data ?? [], lockType: true })}
                    initial={feedbackPrefillFromPayload(recording.payload, feedbackTypeOf(recording.form_name))}
                    subtitle="Prefilled from the website form — check it, then save. Saving files it in the feedback register and gives it its reference."
                    onClose={() => setRecording(null)}
                    onSaved={(saved) => void recordInRegister(recording, saved)} />
            )}

            {error && <div className="error-banner">{error}</div>}

            {groups.length === 0 && (
                <div className="card"><div className="card-body empty">
                    Nothing waiting. Referral submissions go straight to the Referrals page;
                    everything else from the website arrives here.
                </div></div>
            )}

            {groups.map(([form, rows]) => (
                <div key={form} className="card">
                    <div className="card-head">
                        <h2 style={{ textTransform: "capitalize" }}>{form.replace(/[-_]+/g, " ")}</h2>
                        <span className="chip info">{rows.length} pending</span>
                    </div>
                    <div className="card-body">
                        <div style={{ display: "grid", gap: "0.5rem" }}>
                            {rows.map((item) => (
                                <div key={item.id} className="file-row" style={{ alignItems: "flex-start" }}>
                                    <span className="mono" style={{ whiteSpace: "nowrap" }}>{formatDateTime(item.received_at)}</span>
                                    <span style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 600 }}>
                                            {item.ref_code && (
                                                <span className="chip mute mono" style={{ marginRight: 6, fontWeight: 700 }}>{item.ref_code}</span>
                                            )}
                                            {senderOf(item.payload)}
                                            {containsLink(item.payload) && (
                                                <span className="chip warn" style={{ marginLeft: 6 }}
                                                    title="This submission contains a web address. It is shown as plain text and is not clickable — do not open it unless you trust the sender.">
                                                    Contains a link
                                                </span>
                                            )}
                                        </div>
                                        {open === item.id && (
                                            <dl className="kv" style={{ marginTop: "0.5rem" }}>
                                                {detailsOf(item.payload).map(([k, v]) => (
                                                    <span key={k} style={{ display: "contents" }}>
                                                        <dt style={{ textTransform: "capitalize" }}>{k}</dt>
                                                        <dd style={{ whiteSpace: "pre-wrap" }}>{v}</dd>
                                                    </span>
                                                ))}
                                            </dl>
                                        )}
                                    </span>
                                    <span style={{ display: "flex", gap: "0.35rem", whiteSpace: "nowrap" }}>
                                        <button className="icon-btn" onClick={() => setOpen(open === item.id ? null : item.id)}>
                                            {open === item.id ? "Hide details" : "Details"}
                                        </button>
                                        {isFeedbackForm(item.form_name) && (
                                            <button className="icon-btn" onClick={() => setRecording(item)}
                                                title="File this in the feedback register, where it gets its reference">
                                                Record in register
                                            </button>
                                        )}
                                        <button className="icon-btn danger" onClick={() => setDismissing(item)}>Dismiss</button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};
